// Ready-made MCP / AI-agent trading prompts shown in the bot's "Trigger this Bot" MCP tab.
// Each object is self-contained: its stable id, the KPI badges (real backtest figures,
// TradingView-style Max Drawdown), AutoTrading gating, and build(botId) returning the full
// prompt with the bot's id substituted for {{BOT_ID}}. The Edit Bot MCP tab renders these
// dynamically. Prompts with autoTradingOnly: true are only shown to AutoTrading members.
//
// VERSIONING: every time a prompt's content changes, bump TWO fields on that object:
//   (1) id          — increment the trailing number, e.g. 'TR-GC-Crypto-2' -> 'TR-GC-Crypto-3'
//   (2) lastUpdated — UTC date+time of the change, format 'YYYY-MM-DD HH:MM'
// The id is stamped into the prompt body via build(botId, promptId), so it versions the
// deployed prompt for traceability.

export const mcpPrompts = [
  {
    id: 'TR-GC-Crypto-4',
    lastUpdated: '2026-06-10 20:01',
    title: 'Trend Radar - Gaussian Channel - Crypto',
    subtitle: 'Upper-band crossover on the GC Trend Radar (top 50).',
    autoTradingOnly: false,
    kpis: { pnl: '+5,265%', maxDrawdown: '44.1%', timeframe: '1D', period: '2018-01-01 → 2026-05-21' },
    build (botId, promptId) {
      return `You are managing a crypto portfolio via these rules (${promptId}).
The entry/exit signal is the GC upper-band crossover; the breakout only SIZES the bet, it is NOT required.

1) Fetch all assets/positions (holdings) from my Signum Bot with ID = ${botId} and convert them to USD so you have total NAV.

2) Fetch the latest daily Crypto Trend Radar data for the "gc" detector WITH includeIndicators=true.
If you received less than 50 line items from the MCP, the fetch didn't go through properly, try to fetch it again up to 3 times and don't work with less line items as it can cause missed buy signals or wrong sell signals.
Each asset's "indicators.data[]" holds the last closed candles, each with ohlc.c (close) and gc.upper (the UPPER GC BAND). The asset row also carries breakoutDate.

3) Iterate through all Bot holdings (excluding fiat and stablecoins) and SELL 100% of a holding when the corresponding asset's CLOSE crossed BELOW the upper GC band (today or yesterday), or when the holding is no longer in the Trend Radar results (drifted out).
Sell into USDC, USDT or USD in this priority order.

4) Iterate through the Trend Radar assets (sort by market rank ascending, take the first 50) and BUY an asset when its CLOSE crossed ABOVE the upper GC band (today or yesterday).
Skip any asset you already hold (one position per coin) unless it's dust.
SIZE the buy by breakout recency:
  - if the asset has a breakout whose breakoutDate is within the last 25 calendar days -> buy 8% of NAV
  - otherwise (older breakout, or none) -> buy 2% of NAV
A breakout is NOT required to buy - it only sets the size. Min 1%, don't buy with dust.
Set 1x leverage if it's a Perpetual Futures Bot.
If the indicator data needed to evaluate the crossover is missing, skip that asset.

5) Fetch the Bot assets/positions again and check that the trades you've made resulted in the expected outcome. If not, please fix it.

How to tell my Bot which trading pair to trade?
A Bot is connected to an exchange, and that exchange offers a list of trading pairs, not more, not less.
You have to fetch the available trading pairs my Bot has access to and use the one, which represents the asset we want to trade and our available holdings the best.
Guard against ticker collisions (same symbol, different coin): before trading a pair, call get-pair-price and compare priceUsd to the asset's USD reference — indicators.cmcRefPriceUsd, or the newest indicators.data[].ohlc.c if absent. SKIP the asset and tell the user if they differ by >50% (abs(priceUsd - ref) / ref > 0.5), or if priceUsd or the reference is missing.
Edit my Bot and set that trading pair before sending the trading signal.

If you want to buy a trading pair with a quote coin we don't have holdings for, go ahead and convert from the stablecoins/fiat to the quote coin you need, but only the required amount you want to trade with (not more) to avoid paying more trading fees than needed. Prefer staying in stablecoins and only use fiat when there's no other option. If no direct conversion pair is available on the exchange, skip the buy and inform the user.
If you cannot find a matching trading pair, you have to skip that asset and move to the next one - inform the user in the chat which ones you had to skip.

How to send buy/sell signals to Signum?
Before sending a trading signal edit the Bot and set the trading pair to the one you want to trade - you have no other way to tell the Bot what to trade.

How to calculate order_size?
When sending a percentage value for order_size, remember that eg. 2% means "2% of the base or quote coin" not 2% of NAV.
If you want to trade eg. 2% of NAV, you have to calculate NAV first and use that as your basis for the percentage calculation.`
    }
  },
  {
    id: 'TR-B2S-Crypto-5',
    lastUpdated: '2026-06-10 20:01',
    title: 'Trend Radar - Bot2Slow - Crypto',
    subtitle: 'Slow-line crossover on the B2S Trend Radar (top 100), breakout-recency sizing.',
    autoTradingOnly: true,
    kpis: { pnl: '+10,231%', maxDrawdown: '39.8%', timeframe: '1D', period: '2018-01-01 → 2026-05-21' },
    build (botId, promptId) {
      return `You are managing a crypto portfolio via these rules (${promptId}).

1) Fetch all assets/positions (holdings) from my Signum Bot with ID = ${botId} and convert them to USD so you have total NAV.

2) Fetch the latest daily Crypto Trend Radar data for the "b2s" detector WITH includeIndicators=true.
If you received less than 100 line items from the MCP, the fetch didn't go through properly, try to fetch it again up to 3 times and don't work with less line items as it can cause missed buy signals or wrong sell signals.
Each asset's "indicators.data[]" holds the last closed candles, each with ohlc.c (close) and b2s.slow_line. The asset row also carries breakoutDate.

3) Iterate through all Bot holdings (excluding fiat and stablecoins) and SELL 100% of a holding when the corresponding asset's CLOSE crossed BELOW the slow_line (today or yesterday), or when the holding is no longer in the Trend Radar results (drifted out).
Sell into USDC, USDT or USD in this priority order.

4) Iterate through the Trend Radar assets (sort by market rank ascending, take the first 100) and BUY an asset when its CLOSE crossed ABOVE the slow_line (today or yesterday).
Skip any asset you already hold (one position per coin) unless it's dust.
SIZE the buy by breakout recency:
  - if the asset has a breakout whose breakoutDate is within the last 25 calendar days -> buy 8% of NAV
  - otherwise (older breakout, or none) -> buy 2% of NAV
A breakout is NOT required to buy - it only sets the size. Min 1%, don't buy with dust.
Set 1x leverage if it's a Perpetual Futures Bot.
If the indicator data needed to evaluate the crossover is missing, skip that asset.

5) Fetch the Bot assets/positions again and check that the trades you've made resulted in the expected outcome. If not, please fix it.

6) Send me a summary email once you are done.

How to tell my Bot which trading pair to trade?
A Bot is connected to an exchange, and that exchange offers a list of trading pairs, not more, not less.
You have to fetch the available trading pairs my Bot has access to and use the one, which represents the asset we want to trade and our available holdings the best.
Guard against ticker collisions (same symbol, different coin): before trading a pair, call get-pair-price and compare priceUsd to the asset's USD reference — indicators.cmcRefPriceUsd, or the newest indicators.data[].ohlc.c if absent. SKIP the asset and tell the user if they differ by >50% (abs(priceUsd - ref) / ref > 0.5), or if priceUsd or the reference is missing.
Edit my Bot and set that trading pair before sending the trading signal.

If you want to buy a trading pair with a quote coin we don't have holdings for, go ahead and convert from the stablecoins/fiat to the quote coin you need, but only the required amount you want to trade with (not more) to avoid paying more trading fees than needed. Prefer staying in stablecoins and only use fiat when there's no other option. If no direct conversion pair is available on the exchange, skip the buy and inform the user.
If you cannot find a matching trading pair, you have to skip that asset and move to the next one - inform the user in the chat which ones you had to skip.

How to send buy/sell signals to Signum?
Before sending a trading signal edit the Bot and set the trading pair to the one you want to trade - you have no other way to tell the Bot what to trade.

How to calculate order_size?
When sending a percentage value for order_size, remember that eg. 2% means "2% of the base or quote coin" not 2% of NAV.
If you want to trade eg. 2% of NAV, you have to calculate NAV first and use that as your basis for the percentage calculation.`
    }
  },
  {
    id: 'TR-B2S-Stocks-3',
    lastUpdated: '2026-06-10 20:01',
    title: 'Trend Trader - Bot2Slow - Stocks',
    subtitle: 'Green-flip entry on the B2S Stocks Trend Radar (top 150).',
    autoTradingOnly: true,
    kpis: { pnl: '+506%', maxDrawdown: '16.3%', timeframe: '1D', period: '2018-01-01 → 2026-05-21' },
    build (botId, promptId) {
      return `You are managing a stocks portfolio via these rules (${promptId}).

1) Fetch all assets/positions (holdings) from my Signum Bot with ID = ${botId} and convert them to USD so you have total NAV.

2) Fetch the latest daily Stocks Trend Radar data for the "b2s" detector WITH includeIndicators=true.
If you received less than 150 line items from the MCP, the fetch didn't go through properly, try to fetch it again up to 3 times and don't work with less line items as it can cause missed buy signals or wrong sell signals.
Each asset's "indicators.data[]" holds the last closed candles, each with ohlc.c (close).

3) Iterate through all of the Bot holdings (excluding fiat and stablecoins) and SELL 100% of a holding when the corresponding Trend Radar asset's trend is NO LONGER GREEN - i.e. it is currently Grey or Red (or it left Green today/yesterday) - or when the holding is no longer in the Trend Radar results (drifted out).
Sell into USDC.

4) Iterate through Trend Radar assets (sort by market rank ascending, take the first 150) and BUY an asset when its trend (trendChanged) flipped green today/yesterday.
Skip any asset you already hold (one position per coin) unless it's dust.
Buy with 5% of NAV (min 1%, don't buy with dust).
Set 1x leverage if it's a Perpetual Futures Bot.

5) Fetch the Bot assets/positions again and check if the trades you've made resulted in the expected outcome. If not, please fix it.

6) Send me a summary email once you are done.

How to tell my Bot which trading pair to trade?
A Bot is connected to an exchange, and that exchange offers a list of trading pairs, not more, not less.
You have to fetch the available trading pairs my Bot has access to and use the one, which represents the asset we want to trade and our available holdings the best. Most likely you will be connected on the Hyperliquid exchange, which offers tokenized stocks via the "XYZ-" subexchange - e.g. XYZ-AAPL/USDC stands for the AAPL ticker.
Guard against ticker collisions (same symbol, different asset): before trading a pair, call get-pair-price and compare priceUsd to the asset's newest indicators.data[].ohlc.c (USD). SKIP the asset and tell the user if they differ by >50% (abs(priceUsd - close) / close > 0.5), or if priceUsd or the close is missing.
Edit my Bot and set that trading pair before sending the trading signal.
If you cannot find a matching trading pair, you have to skip that asset and move to the next one - inform the user in the chat which ones you had to skip.

How to send buy/sell signals to Signum?
Before sending a trading signal edit the Bot and set the trading pair to the one you want to trade - you have no other way to tell the Bot what to trade.

How to calculate order_size?
When sending a percentage value for order_size, remember that eg. 2% means "2% of the base or quote coin" not 2% of NAV.
If you want to trade eg. 2% of NAV, you have to calculate NAV first and use that as your basis for the percentage calculation.`
    }
  }
]

